import {
  Cancel01Icon,
  HelpCircleIcon,
  Mail01Icon,
  Note01Icon,
  Pin02Icon,
  PropertyViewIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { useLocale } from 'hooks/locale';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';

import { Badge } from 'components/Badge/Badge';
import { Button } from 'components/FormComponents';

import { checkHistoryActions } from './../../constants/check-history-actions';
import styles from './HistoryRow.module.scss';

export const HistoryRow = ({
  entry,
  candidate,
  companyChecks,
  referees,
  className,
  companyUsers = [],
  company,
  disableLinks = false,
  pinned = false,
  isPinnedSection = false,
  isExiting = false,
  onTogglePin,
  onView,
  onDetails,
  onViewEmailTemplate,
  detailsDrawerOpen = false,
  onCandidateRequest = () => {},
  onRefereeRequest = () => {},
}) => {
  const { t } = useTranslation();
  const { dateSlashFormat } = useLocale(company?.locale);

  // Derived values
  const checkHuman = entry?.check_type
    ? companyChecks?.find(companyCheck => companyCheck.type === entry.check_type)?.check_type.title
    : null;

  const userName = <b className={styles.userRef}>@{entry.user_name || 'User'}&#160;</b>;

  const refCandidate = disableLinks ? (
    <b className={cn(styles.userRef, 'u-padding-right--xSmall')}>{candidate.name}</b>
  ) : (
    <Link to={`/check/${candidate.id}/candidate`}>{candidate.name}</Link>
  );

  const country =
    entry.country && entry.country !== 'Not specified.' ? `from ${entry.country}` : '';
  const reportType = entry.check_type || null;

  const referee = entry.referee_id ? referees.find(r => r.id === entry.referee_id) : null;

  const refReferee = referee ? (
    disableLinks ? (
      <b className={cn(styles.userRef, 'u-padding-right--xSmall')}>{referee.name}</b>
    ) : (
      <Link
        to={`/check/${candidate.id}/${
          reportType === 'custom_reference' ? 'reference' : 'referees'
        }/${referee.id}`}>
        {referee.name}
      </Link>
    )
  ) : null;

  const referenceLink =
    referee && !disableLinks ? (
      <Link
        to={`/check/${candidate.id}/${
          reportType === 'custom_reference' ? 'reference' : 'reports'
        }/${referee.id}`}>
        View check
      </Link>
    ) : null;

  // For CompletedCandidate case: joining multiple referee links if needed
  const candidateRefereeLinks =
    referees && referees.length > 0
      ? referees.map((ref, index) => (
          <span key={ref.id}>
            {index > 0 && ' & '}
            {disableLinks ? (
              <b className={cn(styles.userRef, 'u-padding-right--xSmall')}>{ref.name}</b>
            ) : (
              <Link
                to={`/check/${candidate.id}/${
                  reportType === 'custom_reference' ? 'reference' : 'referees'
                }/${ref.id}`}>
                {ref.name}
              </Link>
            )}
          </span>
        ))
      : null;

  // Build the data object to pass into our mapping function
  const actionData = {
    refCandidate,
    refReferee,
    referenceLink,
    candidateRefereeLinks,
    country,
    styles,
    userName,
    candidate,
    checkHuman,
    entry,
    referee,
    onCandidateRequest,
    onRefereeRequest,
    referees,
    companyUsers,
  };

  // Use the mapping constant to get the action
  const action = checkHistoryActions[entry.type]
    ? checkHistoryActions[entry.type](actionData)
    : { icon: HelpCircleIcon, description: <p>Unknown action.</p> };

  // Map color values to Tailwind classes
  const colorMap = {
    'green-2': 'text-green-2',
    'yellow-2': 'text-yellow-2',
    'blue-2': 'text-blue-2',
    'red-2': 'text-red-2',
    'primary-4': 'text-primary-4',
    'orange-2': 'text-orange-2',
  };
  const iconColorClass = action.color
    ? colorMap[action.color] || 'text-text-primary'
    : 'text-text-primary';

  const isPinnedGhost = pinned && !isPinnedSection;

  const cardClasses = cn(
    'group relative flex flex-row gap-4 items-center rounded-xl',
    isPinnedGhost
      ? [
          '!py-3 !px-5 shadow-sm shadow-soft',
          '!border-2 !border-dashed !border-primary-4/35',
          '!bg-[rgba(226,224,249,0.22)]',
          '!cursor-default',
          'transition-[background-color,border-color,box-shadow,transform] duration-300 ease-out',
          '[&>*:not(:last-child)]:opacity-[0.65]',
          'hover:!shadow-none hover:!translate-y-0',
        ]
      : [
          'p-8 shadow-sm shadow-soft',
          'transition-[box-shadow,transform] duration-200',
          'hover:shadow-md hover:-translate-y-0.5',
        ],
    isExiting && '!pointer-events-none',
    {
      [styles.pinnedSection]: pinned && isPinnedSection,
      [styles.pinnedSectionExiting]: isExiting && isPinnedSection,
      'border border-border-primary bg-surface-primary': !pinned,
    },
    className,
  );

  return (
    <div className={cardClasses} data-testid={`history-entry-type-${entry.type}`}>
      <HugeiconsIcon icon={action.icon} size="24" strokeWidth={1.5} className={iconColorClass} />
      <div className="flex flex-row gap-8">
        <span className="text-neutral-7 font-medium text-2xl">
          <Moment format={dateSlashFormat}>{entry.created_at}</Moment>
        </span>
        <span className="text-neutral-7 font-medium text-2xl">
          <Moment format="@HH:mm">{entry.created_at}</Moment>
        </span>
      </div>
      <div className="text-text-primary font-regular text-2xl ml-8 flex-1">
        {action.description}
      </div>

      {/* Ghost card (pinned item in ALL HISTORY): badge only, no hover actions */}
      {pinned && !isPinnedSection && (
        <div className="ml-auto shrink-0">
          <span className={cn(styles.pinnedBadge, { [styles.pinnedBadgeExiting]: isExiting })}>
            <Badge text="Pinned" className="!bg-primary-4 !text-white" />
          </span>
        </div>
      )}

      {/* Pinned-section unpin button — always visible (not hover-gated) */}
      {onTogglePin && pinned && isPinnedSection && (
        <Button
          className={cn(styles.pinBtn, styles.pinBtnActive)}
          onClick={() => onTogglePin(entry)}
          tip="Unpin">
          <span className={styles.pinBtnDefaultIcon}>
            <HugeiconsIcon
              icon={Pin02Icon}
              size="20"
              strokeWidth={1.5}
              className={styles.pinBtnActiveIcon}
            />
          </span>
          <span className={styles.pinBtnHoverIcon}>
            <HugeiconsIcon icon={Cancel01Icon} size="20" strokeWidth={1.5} />
          </span>
        </Button>
      )}

      {/* Hover action group: View, Details, Email template, Pin. Always visible on touch/small screens (max-md). */}
      {!(pinned && !isPinnedSection) &&
        (onView || onDetails || onViewEmailTemplate || (onTogglePin && !pinned)) && (
          <div className="ml-auto shrink-0 opacity-0 group-hover:opacity-100 max-md:opacity-100 transition-opacity duration-150 flex items-center gap-[8px]">
            {onView && (
              <Button className={styles.pinBtn} onClick={() => onView(entry)} tip="Go to Check">
                <HugeiconsIcon icon={PropertyViewIcon} size="20" strokeWidth={1.5} />
              </Button>
            )}
            {onViewEmailTemplate && (
              <Button
                className={styles.pinBtn}
                onClick={() => onViewEmailTemplate(entry)}
                tip={t('historyEmailTemplate.viewLink')}>
                <HugeiconsIcon icon={Mail01Icon} size="20" strokeWidth={1.5} />
              </Button>
            )}
            {onDetails && (
              <Button
                className={styles.pinBtn}
                onClick={() => onDetails(entry)}
                tip="Details"
                showTooltip={!detailsDrawerOpen}>
                <HugeiconsIcon icon={Note01Icon} size="20" strokeWidth={1.5} />
              </Button>
            )}
            {onTogglePin && !pinned && (
              <Button className={styles.pinBtn} onClick={() => onTogglePin(entry)} tip="Pin">
                <HugeiconsIcon icon={Pin02Icon} size="20" strokeWidth={1.5} />
              </Button>
            )}
          </div>
        )}
    </div>
  );
};
